import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdSearch, MdAdd, MdEdit, MdDelete, MdContentCopy, MdVisibility, MdToggleOn, MdToggleOff, MdSecurity, MdPeople, MdLocationOn, MdVpnKey } from 'react-icons/md';
import {
  getAllRoles,
  deleteRole,
  activateRole,
  deactivateRole,
  cloneRole,
  getRoleUsers
} from '../../../api/roles';
import { getRoleFromToken } from '../../../utils/jwtUtils';
import './RoleList.css';
import './BookRoom.css';

function RoleList() {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [filteredRoles, setFilteredRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedRole, setSelectedRole] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloneName, setCloneName] = useState('');
  const [userRole, setUserRole] = useState('');

  // Check if current user is SuperAdmin
  useEffect(() => {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    if (token) {
      const claims = getRoleFromToken(token);
      setUserRole(claims?.role?.toUpperCase() || '');
    }
  }, []);

  const isSuperAdmin = userRole === 'SUPER_ADMIN';

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    filterRoles();
  }, [roles, searchTerm, statusFilter, typeFilter]);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const data = await getAllRoles();
      setRoles(data);
      setError(null);
    } catch (err) {
      setError('Failed to load roles: ' + err.message);
      console.error('Error fetching roles:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterRoles = () => {
    let filtered = [...roles];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(role =>
        role.roleName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        role.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(role =>
        statusFilter === 'active' ? role.active : !role.active
      );
    }

    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(role =>
        typeFilter === 'system' ? role.isSystemRole : !role.isSystemRole
      );
    }

    setFilteredRoles(filtered);
  };

  const handleCreateRole = () => {
    navigate('/admin/roles/builder');
  };

  const handleEditRole = (roleId) => {
    navigate(`/admin/roles/builder/${roleId}`);
  };

  const handleViewPermissions = (roleId) => {
    navigate(`/admin/roles/${roleId}/permissions`);
  };

  const handleToggleStatus = async (role) => {
    try {
      if (role.active) {
        await deactivateRole(role.id);
      } else {
        await activateRole(role.id);
      }
      fetchRoles();
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      alert('Failed to update role status: ' + errorMessage);
    }
  };

  const handleDeleteClick = (role) => {
    setSelectedRole(role);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteRole(selectedRole.id);
      setShowDeleteModal(false);
      setSelectedRole(null);
      fetchRoles();
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      alert('Failed to delete role: ' + errorMessage);
    }
  };

  const handleCloneClick = (role) => {
    setSelectedRole(role);
    setCloneName(role.roleName + ' (Copy)');
    setShowCloneModal(true);
  };

  const handleCloneConfirm = async () => {
    try {
      await cloneRole(selectedRole.id, cloneName);
      setShowCloneModal(false);
      setSelectedRole(null);
      setCloneName('');
      fetchRoles();
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      alert('Failed to clone role: ' + errorMessage);
    }
  };

  const handleViewUsers = async (roleId) => {
    try {
      const users = await getRoleUsers(roleId);
      alert(`Users with this role:\n${users.map(u => u.email).join('\n') || 'No users assigned'}`);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      alert('Failed to load users: ' + errorMessage);
    }
  };

  if (loading) {
    return <div className="role-list-loading">Loading roles...</div>;
  }

  if (error) {
    return (
      <div className="role-list-error">
        <p>{error}</p>
        <button onClick={fetchRoles}>Retry</button>
      </div>
    );
  }

  return (
    <div className="role-list-container">
      {/* Compact Header */}
      <div className="page-header-compact">
        <div className="header-left-compact">
          <h2 className="page-title-compact">
            <MdSecurity className="title-icon" />
            Role Management
          </h2>
        </div>

        <div className="header-filters-compact">
          <div className="input search-compact">
            <span className="icon"><MdSearch /></span>
            <input
              placeholder="Search roles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select-compact"
            style={{ marginLeft: '0.5rem' }}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="filter-select-compact"
            style={{ marginLeft: '0.5rem' }}
          >
            <option value="all">All Types</option>
            <option value="custom">Custom</option>
            <option value="system">System</option>
          </select>

          <button
            className="btn-book-compact"
            onClick={handleCreateRole}
            style={{ marginLeft: '0.5rem' }}
          >
            <MdAdd />
            <span>Create Role</span>
          </button>
        </div>
      </div>

      <div className="filter-stats" style={{ padding: '10px 20px', background: 'white', marginBottom: '20px', borderRadius: '8px' }}>
        Showing {filteredRoles.length} of {roles.length} roles
      </div>

      <div className="roles-grid">
        {filteredRoles.map(role => (
          <div key={role.id} className={`role-card ${!role.active ? 'inactive' : ''}`}>
            <div className="role-card-header">
              <div>
                <h3>{role.roleName}</h3>
                {role.isSystemRole && <span className="badge badge-system">System Role</span>}
                <span className={`badge ${role.active ? 'badge-active' : 'badge-inactive'}`}>
                  {role.active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            <div className="role-card-body">
              <p className="role-description">{role.description || 'No description'}</p>

              <div className="role-stats">
                <div className="stat-item">
                  <MdVpnKey style={{ marginRight: '4px', color: '#667eea' }} />
                  <span className="stat-label">Permissions:</span>
                  <span className="stat-value">{role.permissionCount || 0}</span>
                </div>
                <div className="stat-item">
                  <MdPeople style={{ marginRight: '4px', color: '#10b981' }} />
                  <span className="stat-label">Users:</span>
                  <span className="stat-value">{role.userCount || 0}</span>
                </div>
                <div className="stat-item">
                  <MdLocationOn style={{ marginRight: '4px', color: '#f59e0b' }} />
                  <span className="stat-label">Locations:</span>
                  <span className="stat-value">
                    {role.assignedLocations?.split(',').filter(Boolean).length || 0}
                  </span>
                </div>
              </div>

              {role.assignedLocations && (
                <div className="role-locations">
                  <strong>Locations:</strong> {role.assignedLocations}
                </div>
              )}

              <div className="role-metadata">
                <small>Created: {new Date(role.createdAt).toLocaleDateString()}</small>
              </div>
            </div>

            <div className="role-card-actions">
              <button
                className="btn-action btn-primary-small"
                onClick={() => handleViewPermissions(role.id)}
                title="View Permissions"
              >
                <MdVpnKey />
              </button>
              <button
                className="btn-action btn-secondary-small"
                onClick={() => handleEditRole(role.id)}
                disabled={role.isSystemRole && !isSuperAdmin}
                title={role.isSystemRole ? (isSuperAdmin ? "Edit System Role Permissions" : "System roles cannot be edited") : "Edit Role"}
              >
                <MdEdit />
              </button>
              <button
                className="btn-action btn-secondary-small"
                onClick={() => handleCloneClick(role)}
                title="Clone Role"
              >
                <MdContentCopy />
              </button>
              <button
                className="btn-action btn-secondary-small"
                onClick={() => handleToggleStatus(role)}
                disabled={role.isSystemRole}
                title={role.active ? 'Deactivate' : 'Activate'}
              >
                {role.active ? <MdToggleOff /> : <MdToggleOn />}
              </button>
              <button
                className="btn-action btn-secondary-small"
                onClick={() => handleViewUsers(role.id)}
                title="View Users"
              >
                <MdVisibility />
              </button>
              <button
                className="btn-action btn-danger-small"
                onClick={() => handleDeleteClick(role)}
                disabled={role.isSystemRole || role.userCount > 0}
                title="Delete Role"
              >
                <MdDelete />
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredRoles.length === 0 && (
        <div className="no-roles">
          <p>No roles found matching your criteria.</p>
          <button onClick={handleCreateRole}>Create Your First Role</button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Confirm Delete</h2>
            <p>Are you sure you want to delete the role "{selectedRole?.roleName}"?</p>
            <p className="warning-text">This action cannot be undone.</p>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
              <button className="btn-danger" onClick={handleDeleteConfirm}>
                Delete Role
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clone Role Modal */}
      {showCloneModal && (
        <div className="modal-overlay" onClick={() => setShowCloneModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Clone Role</h2>
            <p>Enter a name for the cloned role:</p>
            <input
              type="text"
              value={cloneName}
              onChange={(e) => setCloneName(e.target.value)}
              placeholder="New role name"
              className="modal-input"
            />
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowCloneModal(false)}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleCloneConfirm}
                disabled={!cloneName.trim()}
              >
                Clone Role
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RoleList;
